import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getSchedule, getLeaderboard } from '../api';
import { rigaDate, rigaTime } from '../format';

interface Hit { type: 'team' | 'player' | 'match'; title: string; subtitle?: string; to: string; flag?: string }

const PLAYERS = ['Armands', 'Edgars', 'Valters', 'Emīls', 'Einārs', 'Raivis', 'Edijs', 'Jānis'];

export default function GlobalSearch() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const { data: schedule } = useQuery({ queryKey: ['schedule'], queryFn: getSchedule, enabled: open });
  const { data: lb } = useQuery({ queryKey: ['leaderboard'], queryFn: getLeaderboard, enabled: open });

  // Keyboard '/' to open
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const target = e.target as HTMLElement;
      const typing = target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable);
      if (e.key === '/' && !typing) { e.preventDefault(); setOpen(true); }
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => { if (open) setTimeout(() => inputRef.current?.focus(), 30); }, [open]);

  const hits = useMemo<Hit[]>(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return [];
    const out: Hit[] = [];

    // Players
    for (const p of PLAYERS) {
      if (p.toLowerCase().includes(needle)) out.push({ type: 'player', title: p, to: `/players/${encodeURIComponent(p)}` });
    }

    // Teams from standings (via lb.standings team chips)
    const teamSet = new Map<string, { name: string; flag: string }>();
    for (const s of lb?.standings ?? []) {
      for (const t of s.teams) teamSet.set(t.code, { name: t.name, flag: t.flag });
    }
    for (const [code, t] of teamSet) {
      if (code.toLowerCase().includes(needle) || t.name.toLowerCase().includes(needle)) {
        out.push({ type: 'team', title: `${t.flag} ${t.name}`, subtitle: code, to: `/teams/${code}`, flag: t.flag });
      }
    }

    // Matches
    for (const m of schedule?.matches ?? []) {
      const a = m.home_name.toLowerCase();
      const b = m.away_name.toLowerCase();
      if (a.includes(needle) || b.includes(needle)) {
        out.push({
          type: 'match',
          title: `${m.home_name} vs ${m.away_name}`,
          subtitle: `${rigaDate(m.utc_kickoff)} · ${rigaTime(m.utc_kickoff)} · ${m.stage}`,
          to: `/matches/${m.id}`,
        });
      }
    }

    return out.slice(0, 30);
  }, [q, lb, schedule]);

  function go(h: Hit) {
    setOpen(false);
    setQ('');
    navigate(h.to);
  }

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-ink-800 text-chalk hover:bg-ink-700 active:bg-ink-600"
        aria-label="Search"
        title="Search (/)"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="11" cy="11" r="7" />
          <path d="M21 21l-4.3-4.3" />
        </svg>
      </button>
      {open && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-start justify-center pt-16 sm:pt-24" onClick={() => setOpen(false)}>
          <div className="card w-full sm:w-[34rem] max-w-[calc(100vw-2rem)] mx-4 p-2" onClick={(e) => e.stopPropagation()} data-allow-select>
            <input
              ref={inputRef}
              value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Search teams, players, matches…"
              className="w-full bg-transparent px-3 py-2 text-sm text-chalk focus:outline-none"
            />
            {hits.length > 0 && (
              <ul className="max-h-[60vh] overflow-y-auto divide-y divide-ink-800 mt-1">
                {hits.map((h, i) => (
                  <li key={i}>
                    <button
                      onClick={() => go(h)}
                      className="w-full text-left px-3 py-2.5 hover:bg-ink-800 rounded-md flex items-center gap-2"
                    >
                      <span className="text-[10px] uppercase tracking-wider text-dim w-12 shrink-0">{h.type}</span>
                      <span className="text-sm flex-1">{h.title}</span>
                      {h.subtitle && <span className="text-[10px] text-dim">{h.subtitle}</span>}
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {q.trim() && hits.length === 0 && (
              <div className="px-3 py-4 text-xs text-dim text-center">No matches.</div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
